import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import { OverheadBucketTaxonomy } from "./OverheadBucketTaxonomy";
import { useManufacturingOverheadBuckets } from "./useManufacturingOverheadBuckets";
import { ManufacturingPricingNotificationSettingsCard } from "./ManufacturingPricingNotificationSettingsCard";
import { useManufacturingPricingNotificationSettings } from "./useManufacturingPricingNotificationSettings";
import { ManufacturingOverheadPeriodValuesCard } from "./ManufacturingOverheadPeriodValuesCard";
import { ManufacturingOverheadCogsLinkCard } from "./ManufacturingOverheadCogsLinkCard";
import { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";
import { ManufacturingOverheadVarianceReportCard } from "./ManufacturingOverheadVarianceReportCard";
import { OverheadPlantPeriodFields } from "./OverheadPlantPeriodFields";

export function ManufacturingOverheadBucketsPanel() {
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const [taxonomyCollapsed, setTaxonomyCollapsed] = useState(true);
  const [screen, setScreen] = useState<"period" | "variance">("period");
  const buckets = useManufacturingOverheadBuckets(gate.enabled, navTick);
  const alertSettings = useManufacturingPricingNotificationSettings(gate.enabled, navTick);
  const periodValues = useManufacturingOverheadPeriodValues(gate.enabled, navTick);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Overhead buckets</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => {
              setScreen("period");
              setTaxonomyCollapsed(false);
              buckets.setShowCreate(true);
              buckets.cancelEdit();
            }}
            disabled={!gate.enabled || gate.loading || buckets.actionBusy}
          >
            + New bucket
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={() => {
              gate.refresh();
              setNavTick((s) => s + 1);
            }}
            disabled={gate.loading || buckets.listBusy}
          >
            {buckets.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <ManufacturingGateBanner gate={gate} />

      {gate.enabled && (buckets.localError || periodValues.localError) ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {buckets.localError ?? periodValues.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          <div role="tablist" aria-label="Overhead screen" className="inline-flex rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-900">
            {([
              ["period", "Period"],
              ["variance", "Variance"],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={screen === id}
                className={
                  "rounded-md px-3 py-1 font-semibold " +
                  (screen === id
                    ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-950 dark:text-zinc-100"
                    : "text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-200")
                }
                onClick={() => setScreen(id)}
              >
                {label}
              </button>
            ))}
          </div>
          {screen === "period" ? (
            <>
              <div className="grid items-start gap-4 lg:grid-cols-2">
                <div className="min-w-0 space-y-4">
                  <SectionCard
                    className="min-w-0"
                    title="Bucket taxonomy"
                    description="Default buckets seed automatically on first load. Bucket definitions are org-wide; per-plant assumed and actual values are edited beside this list. Inter-plant transfers stay empty until a second plant exists."
                    collapsed={taxonomyCollapsed}
                    onCollapsedChange={setTaxonomyCollapsed}
                  >
                    <OverheadBucketTaxonomy
                      buckets={buckets}
                      plantId={periodValues.plantId}
                      upcomingConsumptionOn={periodValues.summary?.consumption.upcoming_on ?? null}
                      consumptionConfigured={periodValues.summary?.consumption.configured === true}
                    />
                  </SectionCard>
                  <ManufacturingOverheadCogsLinkCard
                    enabled={gate.enabled}
                    plantId={periodValues.plantId}
                    periodStart={periodValues.periodStart}
                  />
                </div>
                <div className="min-w-0">
                  <ManufacturingOverheadPeriodValuesCard state={periodValues} />
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <OverheadPlantPeriodFields state={periodValues} />
              <div className="grid items-start gap-4 lg:grid-cols-2">
                <ManufacturingOverheadVarianceReportCard state={periodValues} />
                <ManufacturingPricingNotificationSettingsCard state={alertSettings} />
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
